import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, List, Map, Navigation, ExternalLink } from "lucide-react";

// ── Geocoding via Nominatim (free, no API key) ────────────────────────────────
async function geocodeAddress(address) {
  try {
    const q = encodeURIComponent(address + ", Australia");
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`,
      { headers: { "Accept-Language": "en", "User-Agent": "DRIVN-FREIGHT/1.0" } }
    );
    const data = await res.json();
    if (data.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {}
  return null;
}

// ── Route optimization (nearest-neighbour from current position) ──────────────
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function optimizeRoute(startLat, startLng, stops) {
  const pending = stops.filter((s) => s.lat !== null && !s.done);
  const noCoords = stops.filter((s) => s.lat === null && !s.done);
  const done = stops.filter((s) => s.done);
  const ordered = [];
  let cur = { lat: startLat, lng: startLng };

  while (pending.length > 0) {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < pending.length; i++) {
      const d = haversine(cur.lat, cur.lng, pending[i].lat, pending[i].lng);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    const next = pending.splice(best, 1)[0];
    ordered.push({ ...next, kmFromPrev: bestDist.toFixed(1) });
    cur = next;
  }
  return [...ordered, ...noCoords, ...done];
}

// ── Stop colour helpers ───────────────────────────────────────────────────────
const stopColor = (type, done) => {
  if (done) return "#6b7280";
  if (type === "pickup") return "#facc15";
  if (type === "dropoff") return "#22c55e";
  return "#a78bfa";
};

// ── Leaflet map marker HTML ───────────────────────────────────────────────────
function markerHtml(label, color) {
  return `<div style="background:${color};border:2px solid #fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;color:#000;font-weight:700;font-size:11px;box-shadow:0 2px 8px #0008">${label}</div>`;
}

// ── Google Maps Directions URL with multiple waypoints ────────────────────────
function buildGoogleMapsUrl(userPos, stops) {
  const active = stops.filter((s) => !s.done && s.address);
  if (active.length === 0) return null;
  if (active.length === 1) return `https://maps.google.com/?q=${encodeURIComponent(active[0].address)}`;
  const origin = userPos
    ? `${userPos.lat},${userPos.lng}`
    : encodeURIComponent(active[0].address);
  const dest = encodeURIComponent(active[active.length - 1].address);
  const waypoints = active
    .slice(userPos ? 0 : 1, -1)
    .map((s) => encodeURIComponent(s.address))
    .join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${waypoints ? `&waypoints=${waypoints}` : ""}&travelmode=driving`;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DriverRouteMap() {
  const [jobs, setJobs] = useState([]);
  const [stops, setStops] = useState([]);
  const [tab, setTab] = useState("list");
  const [userPos, setUserPos] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);

  // ── Load active jobs ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs");
      const active = data.filter((j) =>
        ["assigned", "pending", "onboard"].includes(j.status)
      );
      setJobs(active);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Get driver's current position once ─────────────────────────────────────
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}
    );
  }, []);

  // ── Build + geocode stops whenever jobs or position changes ─────────────────
  useEffect(() => {
    if (jobs.length === 0) { setStops([]); return; }

    const buildAndGeocode = async () => {
      setGeocoding(true);
      const raw = [];
      for (const job of jobs) {
        const pickupDone =
          !!job.sender_signature_url ||
          job.status === "onboard" ||
          job.status === "done";
        raw.push({
          id: `${job.id}-pickup`,
          jobRef: job.job_ref,
          jobId: job.id,
          address: job.pickup_address,
          type: "pickup",
          done: pickupDone,
          lat: null,
          lng: null,
        });
        (job.legs || []).forEach((leg, i) => {
          raw.push({
            id: `${job.id}-leg-${i}`,
            jobRef: job.job_ref,
            jobId: job.id,
            address: leg.address,
            type: leg.stop_type || "stop",
            done: !!leg.completed_at,
            lat: null,
            lng: null,
          });
        });
        raw.push({
          id: `${job.id}-dropoff`,
          jobRef: job.job_ref,
          jobId: job.id,
          address: job.dropoff_address,
          type: "dropoff",
          done: job.status === "done",
          lat: null,
          lng: null,
        });
      }

      const geocoded = await Promise.all(
        raw.map(async (stop) => {
          const coords = await geocodeAddress(stop.address);
          return { ...stop, lat: coords?.lat ?? null, lng: coords?.lng ?? null };
        })
      );

      setGeocoding(false);

      if (userPos) {
        setStops(optimizeRoute(userPos.lat, userPos.lng, geocoded));
      } else {
        setStops(geocoded);
      }
    };

    buildAndGeocode();
  }, [jobs, userPos]);

  // ── Initialise / update Leaflet map ────────────────────────────────────────
  useEffect(() => {
    if (tab !== "map" || !mapRef.current || !window.L) return;

    // Create map instance once
    if (!mapInstance.current) {
      const center = userPos
        ? [userPos.lat, userPos.lng]
        : [-27.4698, 153.0251]; // Brisbane default
      mapInstance.current = window.L.map(mapRef.current).setView(center, 11);
      window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(mapInstance.current);
    }

    const map = mapInstance.current;

    // Remove old markers + polyline
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    if (polylineRef.current) { map.removeLayer(polylineRef.current); polylineRef.current = null; }

    const bounds = [];

    // Driver position marker
    if (userPos) {
      const m = window.L.marker([userPos.lat, userPos.lng], {
        icon: window.L.divIcon({
          className: "",
          html: `<div style="background:#3b82f6;border:3px solid #fff;border-radius:50%;width:18px;height:18px;box-shadow:0 2px 8px #0006"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }),
      })
        .addTo(map)
        .bindPopup("Your Location");
      markersRef.current.push(m);
      bounds.push([userPos.lat, userPos.lng]);
    }

    // Stop markers (numbered by optimized order)
    stops.forEach((stop, i) => {
      if (!stop.lat || !stop.lng) return;
      const color = stopColor(stop.type, stop.done);
      const label = stop.done ? "✓" : String(i + 1);
      const m = window.L.marker([stop.lat, stop.lng], {
        icon: window.L.divIcon({
          className: "",
          html: markerHtml(label, color),
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      })
        .addTo(map)
        .bindPopup(
          `<b>${stop.jobRef}</b> · <span style="text-transform:capitalize">${stop.type}</span><br/>${stop.address}`
        );
      markersRef.current.push(m);
      bounds.push([stop.lat, stop.lng]);
    });

    // Dashed route polyline
    const routePts = [];
    if (userPos) routePts.push([userPos.lat, userPos.lng]);
    stops.filter((s) => s.lat && s.lng).forEach((s) => routePts.push([s.lat, s.lng]));
    if (routePts.length > 1) {
      polylineRef.current = window.L.polyline(routePts, {
        color: "#19e6c8",
        weight: 3,
        opacity: 0.7,
        dashArray: "8 5",
      }).addTo(map);
    }

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [32, 32] });
    }

    // Force size recalc after layout stabilises
    setTimeout(() => map.invalidateSize(), 120);
  }, [tab, stops, userPos]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  const googleMapsUrl = buildGoogleMapsUrl(userPos, stops);
  const activeCount = stops.filter((s) => !s.done).length;

  return (
    <div data-testid="driver-route-page">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="brand-title text-2xl sm:text-3xl">My Route</h1>
        {googleMapsUrl && (
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8", border: "1px solid rgba(25,230,200,0.2)" }}
          >
            <Navigation size={14} /> Navigate All Stops
          </a>
        )}
      </div>
      <p className="text-white/55 text-sm mb-5">
        {geocoding
          ? "Geocoding addresses…"
          : `${activeCount} stop${activeCount !== 1 ? "s" : ""} remaining · ${
              userPos ? "Route optimised from your location" : "Enable GPS for optimised order"
            }`}
      </p>

      {/* ── Tab switcher ── */}
      <div
        className="inline-flex gap-1 p-1 mb-5 rounded-xl border border-white/10"
        style={{ background: "rgba(255,255,255,0.03)" }}
      >
        {[["list", List, "List View"], ["map", Map, "Map View"]].map(([val, Icon, lbl]) => (
          <button
            key={val}
            onClick={() => setTab(val)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === val ? "text-[#0a0a0f]" : "text-white/60 hover:text-white"
            }`}
            style={tab === val ? { background: "#19e6c8" } : {}}
          >
            <Icon size={14} /> {lbl}
          </button>
        ))}
      </div>

      {/* ── List View ── */}
      {tab === "list" && (
        <div className="space-y-2">
          {stops.length === 0 && !geocoding && (
            <div className="text-white/50 text-sm py-4 px-4 rounded-xl border border-white/5 bg-white/[0.02]">
              No active stops. All jobs are complete or no jobs are currently assigned.
            </div>
          )}
          {geocoding && (
            <div className="text-white/50 text-sm py-4 px-4 rounded-xl border border-white/5 bg-white/[0.02] animate-pulse">
              Loading and optimising route…
            </div>
          )}
          {stops.map((stop, i) => {
            const color = stopColor(stop.type, stop.done);
            return (
              <div
                key={stop.id}
                className="flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-white/10 bg-white/[0.02]"
                style={stop.done ? { opacity: 0.45 } : {}}
              >
                {/* Step badge */}
                <div
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: color, color: stop.done ? "#fff" : "#0a0a0f" }}
                >
                  {stop.done ? "✓" : i + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span
                      className="text-xs font-semibold uppercase tracking-widest"
                      style={{ color }}
                    >
                      {stop.type}
                    </span>
                    <span className="text-white/40 text-xs">{stop.jobRef}</span>
                    {stop.done && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">
                        Done
                      </span>
                    )}
                  </div>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(stop.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 hover:text-[#19e6c8] underline decoration-dotted block leading-snug"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {stop.address}
                  </a>
                  {stop.kmFromPrev && !stop.done && (
                    <span className="text-xs text-white/35 mt-0.5 block">
                      {stop.kmFromPrev} km from previous
                    </span>
                  )}
                </div>

                {!stop.done && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(stop.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 p-2.5 rounded-xl transition-opacity hover:opacity-80"
                    style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}
                  >
                    <Navigation size={16} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Map View ── */}
      {tab === "map" && (
        <>
          <div
            className="rounded-2xl overflow-hidden border border-white/10"
            style={{ height: "62vh", minHeight: 340, position: "relative" }}
          >
            <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
            {geocoding && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: "rgba(10,10,15,0.65)", pointerEvents: "none" }}
              >
                <span className="text-white/60 text-sm animate-pulse">Geocoding addresses…</span>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/50">
            {[
              ["#facc15", "Pickup"],
              ["#22c55e", "Drop-off"],
              ["#a78bfa", "Stop"],
              ["#3b82f6", "You"],
              ["#6b7280", "Done"],
            ].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: c }} />
                {l}
              </span>
            ))}
          </div>
        </>
      )}

      {/* ── Empty state when no jobs ── */}
      {jobs.length === 0 && !geocoding && (
        <div className="mt-6 text-center py-8 rounded-2xl border border-white/5 bg-white/[0.02]">
          <MapPin size={32} className="mx-auto mb-3 text-white/20" />
          <p className="text-white/50 text-sm">No active runs assigned to you.</p>
        </div>
      )}
    </div>
  );
}
